import { useEffect, useRef, type FC, type ReactElement, type RefObject } from 'react'

export interface ClickAwayListenerProps {
  onClickAway: (event: Event) => void
  /** Which mouse event triggers click-away detection. @default 'onMouseDown' */
  mouseEvent?: 'onClick' | 'onMouseDown' | 'onMouseUp'
  /** Additional refs whose content should be considered "inside" (e.g. portal content) */
  insideRefs?: RefObject<HTMLElement | null>[]
  children: ReactElement
}

/**
 * Detects clicks outside of the wrapped element and calls `onClickAway`.
 * Drop-in replacement for MUI's ClickAwayListener.
 */
export const ClickAwayListener: FC<ClickAwayListenerProps> = ({
  onClickAway,
  mouseEvent = 'onMouseDown',
  insideRefs,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const eventName = mouseEvent === 'onMouseUp' ? 'mouseup' : mouseEvent === 'onClick' ? 'click' : 'mousedown'
    let touchTriggered = false

    const handler = (event: MouseEvent | TouchEvent) => {
      // Suppress synthetic mouse event after touch to avoid double-fire
      if (event.type === 'touchstart') {
        touchTriggered = true
      } else if (touchTriggered) {
        touchTriggered = false
        return
      }

      const target = event.target as Node
      if (ref.current && !ref.current.contains(target)) {
        const insidePortal = insideRefs?.some(r => r.current?.contains(target))
        if (!insidePortal) {
          onClickAway(event)
        }
      }
    }

    document.addEventListener(eventName, handler)
    document.addEventListener('touchstart', handler)
    return () => {
      document.removeEventListener(eventName, handler)
      document.removeEventListener('touchstart', handler)
    }
  }, [onClickAway, mouseEvent, insideRefs])

  return (
    <div ref={ref} style={{ display: 'contents' }}>
      {children}
    </div>
  )
}
